import { AfterViewInit, Component, ElementRef, HostBinding, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { COLOR_PICKER_MORE_TOOL } from '../../../../data/product/color-pickers.data';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { extractColors } from 'extract-colors';
import { THEMES, TOOLS } from '../../../../data/product/product.data';
import { BLOG } from '../../../../data/blog/blog.data';
import { WALLPAPER_ABOUT } from '../../../../data/tool/tool-about.data';
import { Product } from '../../../../model/product/product.model';
import { URLS } from '../../../../data/navigation/navigation.data';
import { HEX_CODES } from '../../../../data/color/color.data';
import { ColorService } from '../../../../service/color/color.service';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { SeoService } from '../../../../service/seo/seo.service';
import { GRADIENT_SCHEME_MINS, PaletteGenerationService } from '../../../../service/palette/palette-generation.service';
import { PaletteSubtitle } from '../../../../component/palette-widget/palette-widget.component';
import { WALLPAPER_SIZES, WALLPAPER_STYLES, WallpaperService, WallpaperSize, WallpaperStyle, defaultParams, paramsAreDefault, parseParams, styleInfoOf } from '../../../../service/wallpaper/wallpaper.service';
import { CgOption } from '../../../../component/cg-select/cg-select.component';



/**
 * /tools/wallpaper - the procedural wallpaper generator (2026-09-22). The
 * palette comes from the URL exactly like the palette and gradient tools
 * (`/tools/wallpaper/<hexes>` + the source query params), and the picture is
 * fully described by the URL too: `style`, `size`, `art` (the art seed) and the
 * style's two fields, so every wallpaper is a shareable, indexable link. The rendering
 * itself lives in WallpaperService.
 */
@Component( {
  selector: 'app-wallpaper-page',
  templateUrl: './wallpaper-page.component.html',
  styleUrls: [ './wallpaper-page.component.scss', '../tools-page/tools-page.component.scss', '../../plugin-pages/plugins-page/plugins-page.component.scss' ],
} )
export class WallpaperPageComponent implements OnInit, AfterViewInit, OnDestroy {

  TOOLS = TOOLS;
  URLS = URLS;
  STYLES = WALLPAPER_STYLES;
  SIZES = WALLPAPER_SIZES;

  // The palette (path param) + its source (query params), as in the palette tool.
  palette: { value: string }[] = [];
  count = 6;
  private countSynced = false;
  scheme = '';
  foundTheme: any = null;
  initializer?: { colorName: string; value: string };
  fullSeed = '';
  uploadedImg?: { src: string; id: string };

  // The picture: style, size preset, art seed (grain and Shuffle removed, owner 2026-10-12).
  style: WallpaperStyle = 'mesh';
  size: WallpaperSize = WALLPAPER_SIZES[ 0 ];
  artSeed = 0;
  /** the style's three sliders, 0..100 */
  params: number[] = defaultParams( 'mesh' );

  styleOptions: CgOption[] = WALLPAPER_STYLES.map( s => ( { value: s.key, label: s.label } ) );
  sizeOptions: CgOption[] = WALLPAPER_SIZES.map( s => ( { value: s.key, label: `${ s.label }  ·  ${ s.hint }` } ) );

  /** the preview at the chosen size (drawn by the widget): the Preview stage and the size cards */
  previewUrl = '';
  /** the 1920 × 1080 framing: the hero backdrop and the sidebar swatch */
  heroUrl = '';
  downloading = '';
  copied = false;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  // ----- Analysis sections (ported identically from the palette / gradient tools) -----
  channelStats: { rgb: any[]; hsl: any[]; hsv: any[]; cmyk: any[] } | null = null;
  relatedThemeList: { product: Product; category: string }[] = [];
  harmonyResult: any[] = [];
  harmonyScores: { name: string; description: string; score: number; counts: number[] }[] = [];
  shadeTintTone: { title: string; rows: string[][] }[] = [];
  temperatureVariants: { title: string; rows: string[][] }[] = [];
  analogous: number[] = [];
  complementaries: number[] = [];
  triadics: number[] = [];
  temperatures: string[] = [];
  protanomalyColors: string[] = [];
  deuteranomalyColors: string[] = [];
  tritanomalyColors: string[] = [];
  protanopiaColors: string[] = [];
  deuteranopiaColors: string[] = [];
  tritanopiaColors: string[] = [];
  achromatomalyColors: string[] = [];
  achromatopsiaColors: string[] = [];
  copiedKey = '';
  private copiedKeyTimer: ReturnType<typeof setTimeout> | null = null;
  blogCards = [ ...BLOG ].sort( () => Math.random() - 0.5 ).slice( 0, 3 );


  pageSections: { key: string; label: string }[] = [
    { key: 'preview', label: 'Preview' },
    { key: 'channels', label: 'Color Channels' },
    { key: 'harmony', label: 'Harmony' },
    { key: 'shades', label: 'Tints & Shades' },
    { key: 'temperature', label: 'Temperature' },
    { key: 'vision', label: 'Color Vision' },
    { key: 'more', label: 'More Color Tools' },
    { key: 'themes', label: 'Related Themes' },
    { key: 'blog', label: 'From The Blog' },
    { key: 'about', label: 'How It Works' },
    { key: 'reviews', label: 'Reviews' },
  ];

  /** the How It Works closer (the same four questions the server prints for crawlers) */
  readonly aboutLead = WALLPAPER_ABOUT.lead;
  readonly aboutParagraphs = WALLPAPER_ABOUT.paragraphs;
  readonly faq = WALLPAPER_ABOUT.faq;
  /** More Color Tools: the Color Picker */
  readonly colorPicker = COLOR_PICKER_MORE_TOOL;
  readonly moreToolsSource = 'wallpaper-generator';
  activeSection = 'top';
  /** the preview enlarged full screen (the theme page's screenshot lightbox) */
  lightboxOpen = false;
  @ViewChild( 'lightboxDownload' ) lightboxDownload?: ElementRef<HTMLButtonElement>;
  /** what had focus when the lightbox opened, so closing gives it back */
  private lightboxReturnFocus: HTMLElement | null = null;
  private sectionObserver?: IntersectionObserver;

  private editSubject = new Subject<void>();
  private editPending = false;
  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private colorService: ColorService,
    private paletteGen: PaletteGenerationService,
    private analyticsService: AnalyticsService,
    private seoService: SeoService,
    private zone: NgZone,
    private clipboard: Clipboard,
    private wallpaperService: WallpaperService,
  ) {
  }

  ngOnInit(): void {
    this.setLandingSeo();


    this.subs.push( this.route.params.subscribe( params => {
      // /tools/wallpaper/<style>: a landing page per procedure (mesh, waves, ...), a random theme
      // palette drawn in that style; its own indexable head, the URL kept
      const styleLanding = WALLPAPER_STYLES.find( st => st.key === String( params.wallpaper || '' ).toLowerCase() );
      if ( styleLanding ) {
        const query = this.route.snapshot.queryParams;
        // the theme in the link, or a random one on a bare visit: a landing page is an entry
        // point, but the link it writes has to draw the same picture again (owner, 2026-09-22)
        const asked = this.paletteGen.themeByKey( query[ 'theme' ] );
        const t = asked.hexes.length >= 2 ? asked : this.paletteGen.randomTheme();
        const hexes = t.hexes.length >= 2 ? t.hexes : [ '#00baff', '#ba00ff', '#0f172a' ];
        this.count = hexes.length;
        this.countSynced = true;
        this.palette = hexes.map( h => ( { value: '#' + h.replace( '#', '' ).toLowerCase() } ) );
        this.buildChannelStats();
        // the seed, the fields and the size come from the link; the
        // style is the path's, whatever a stray ?style= says
        this.readPictureParams( query );
        this.style = styleLanding.key;
        if ( !query[ 'art' ] ) {
          this.artSeed = this.wallpaperService.randomSeed();
        }
        if ( !query[ 'p' ] ) {
          this.params = defaultParams( this.style );
        }
        this.applySourceParams( t.hexes.length >= 2 ? { theme: t.theme?.key } : {} );
        this.styleLandingKey = styleLanding.key;
        this.setStyleLandingSeo( styleLanding );
        // the palette rides in the router URL while the address bar keeps the landing link, the
        // way the bare page does, so the first interaction writes a complete, shareable URL
        this.navigateTo( hexes, { theme: t.theme?.key }, true, true );
        return;
      }
      this.styleLandingKey = '';
      if ( params.wallpaper ) {
        const hexes = String( params.wallpaper ).split( '-' );
        if ( hexes.length < 2 || hexes.length > 10 || hexes.some( h => !this.colorService.isValidHexCode( h ) ) ) {
          this.router.navigate( [ URLS.notFound ] );
          return;
        }
        const same = this.palette.length === hexes.length
          && this.palette.every( ( p, i ) => p.value.slice( 1 ).toLowerCase() === hexes[ i ].toLowerCase() );
        if ( !same ) {
          this.palette = hexes.map( h => ( { value: '#' + h.toLowerCase() } ) );
          this.buildChannelStats();
          if ( !this.countSynced ) {
            this.count = this.palette.length;
            this.countSynced = true;
          }
        }
        this.readPictureParams( this.route.snapshot.queryParams );
        this.scheduleRender();
      } else {
        // a blank visit: a random Codigrate theme palette (they make the best wallpapers),
        // a random style and seed, rendered in place at the bare URL
        const t = this.paletteGen.randomTheme();
        const hexes = t.hexes.length >= 2 ? t.hexes : [ '#00baff', '#ba00ff', '#0f172a' ];
        const qp: any = t.hexes.length >= 2 ? { theme: t.theme?.key } : {};
        // a style asked for in the URL wins; otherwise the visit gets a random one
        const asked = this.route.snapshot.queryParams[ 'style' ];
        this.style = asked
          ? this.wallpaperService.styleOf( asked )
          : WALLPAPER_STYLES[ Math.floor( Math.random() * WALLPAPER_STYLES.length ) ].key;
        this.params = defaultParams( this.style );
        this.artSeed = this.wallpaperService.randomSeed();
        this.count = hexes.length;
        this.countSynced = true;
        if ( this.palette.length === 0 ) {
          this.palette = hexes.map( h => ( { value: '#' + h.replace( '#', '' ).toLowerCase() } ) );
          this.buildChannelStats();
          this.applySourceParams( qp );
          this.scheduleRender();
        }
        this.navigateTo( hexes, qp, true, true );
      }
      this.refreshSeo();
    } ) );

    this.subs.push( this.route.queryParams.subscribe( params => {
      if ( !this.route.snapshot.params.wallpaper ) {
        return;
      }
      this.applySourceParams( params );
      this.readPictureParams( params );
      if ( params.theme || params.scheme ) {
        this.uploadedImg = undefined;
      }
      this.scheduleRender();
      this.refreshSeo();
    } ) );

    this.subs.push( this.editSubject.pipe( debounceTime( 600 ) ).subscribe( () => {
      if ( !this.editPending ) {
        return;
      }
      this.editPending = false;
      this.navigateTo( this.palette.map( p => p.value ), {} );
    } ) );
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    this.sectionObserver = new IntersectionObserver( entries => {
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) {
            this.zone.run( () => ( this.activeSection = sec ) );
          }
        }
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    setTimeout( () => {
      document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) );
    } );
  }

  ngOnDestroy(): void {
    this.subs.forEach( s => s.unsubscribe() );
    this.sectionObserver?.disconnect();
    if ( this.copiedTimer ) clearTimeout( this.copiedTimer );
    if ( this.copiedKeyTimer ) clearTimeout( this.copiedKeyTimer );
    if ( this.cvdTimer ) clearTimeout( this.cvdTimer );
    if ( this.thumbsTimer ) clearTimeout( this.thumbsTimer );
  }

  // ---------------------------------------------------------------- picture

  /** style / size / art seed / fields from the URL; a missing art seed gets a fresh one */
  private readPictureParams( params: any ): void {
    this.style = this.wallpaperService.styleOf( params.style );
    this.size = this.wallpaperService.sizeOf( params.size );
    const art = parseInt( params.art, 10 );
    this.artSeed = !isNaN( art ) && art > 0 ? art : ( this.artSeed || this.wallpaperService.randomSeed() );
    this.params = parseParams( params.p, this.style );
  }

  private pictureQuery(): any {
    // every key is set (null drops it): the navigation merges query params, so an omitted key
    // would keep its stale value (a size stuck); grain and o, from before they were removed, are dropped
    return {
      style: this.style,
      art: this.artSeed,
      size: this.size.key !== WALLPAPER_SIZES[ 0 ].key ? this.size.key : null,
      grain: null,
      p: paramsAreDefault( this.style, this.params ) ? null : this.params.join( '-' ),
      o: null,
    };
  }

  /** the widget redraws itself from its inputs; nothing to schedule here */
  private scheduleRender(): void {
  }

  onRendered( url: string ): void {
    this.previewUrl = url;
    this.scheduleCvdPictures();
    this.scheduleSizeThumbs();
  }


  /** every size card shows its own real render (a phone spreads the elements; a crop of the desktop picture would not match the stage) */
  sizeThumbs: { [ key: string ]: string } = {};
  private thumbsTimer: any = null;

  private scheduleSizeThumbs(): void {
    if ( this.thumbsTimer ) clearTimeout( this.thumbsTimer );
    this.thumbsTimer = setTimeout( () => {
      this.thumbsTimer = null;
      const canvas = document.createElement( 'canvas' );
      const thumbs: { [ key: string ]: string } = {};
      for ( const sz of WALLPAPER_SIZES ) {
        const scale = 168 / Math.max( sz.width, sz.height );
        canvas.width = Math.max( 24, Math.round( sz.width * scale ) );
        canvas.height = Math.max( 24, Math.round( sz.height * scale ) );
        try {
          this.wallpaperService.render( canvas, this.options() );
          thumbs[ sz.key ] = canvas.toDataURL( 'image/jpeg', 0.8 );
        } catch {
          thumbs[ sz.key ] = '';
        }
      }
      this.sizeThumbs = thumbs;
    }, 80 );
  }

  // ---------------------------------------------------------------- color vision: the picture itself, simulated

  /** the wallpaper redrawn with each deficiency's simulated palette (same seed, same geometry) */
  cvdPictures: { group: string; items: { name: string; note: string; url: string }[] }[] = [];
  private cvdTimer: any = null;

  private scheduleCvdPictures(): void {
    if ( this.cvdTimer ) clearTimeout( this.cvdTimer );
    // after the main preview, off the click path: eight 960px renders
    this.cvdTimer = setTimeout( () => {
      this.cvdTimer = null;
      this.buildCvdPictures();
      this.buildVariantPictures();
    }, 120 );
  }

  /** Tints / Shades / Tones and Warmer / Cooler as pictures: the wallpaper redrawn with each step's palette; a click makes that palette the generator's */
  variantPictures: { title: string; items: { label: string; colors: string[]; url: string }[] }[] = [];
  temperaturePictures: { title: string; items: { label: string; colors: string[]; url: string }[] }[] = [];

  private buildVariantPictures(): void {
    const canvas = document.createElement( 'canvas' );
    canvas.width = 960;
    canvas.height = 540;
    const draw = ( colors: string[] ): string => {
      try {
        this.wallpaperService.render( canvas, { ...this.options(), colors } );
        return canvas.toDataURL( 'image/jpeg', 0.8 );
      } catch {
        return '';
      }
    };
    // the first row of every ramp is the palette itself: the steps after it are the variants
    const pictures = ( groups: { title: string; rows: string[][] }[], word: string ) => groups.map( g => ( {
      title: g.title,
      items: g.rows.slice( 1 ).map( ( row, i ) => ( { label: `${ g.title } ${ i + 1 }`, colors: row, url: draw( row ) } ) ),
    } ) );
    this.variantPictures = pictures( this.shadeTintTone, 'step' );
    this.temperaturePictures = pictures( this.temperatureVariants, 'step' );
  }

  /** a variant card: that palette becomes the generator's (same style, seed and fields) */
  usePalette( colors: string[] ): void {
    this.navigateTo( colors, {} );
    this.analyticsService.logEvent( 'Wallpaper Initialized', { source: 'variant' } );
    this.scrollToSection( 'top' );
  }

  private buildCvdPictures(): void {
    if ( !this.palette.length ) {
      this.cvdPictures = [];
      return;
    }
    // always the 1920 × 1080 design (owner, 2026-09-22), at half size
    const canvas = document.createElement( 'canvas' );
    canvas.width = 960;
    canvas.height = 540;
    this.cvdPictures = this.colorVisionGroups().map( group => ( {
      group: group.group,
      items: group.items.map( item => {
        let url = '';
        try {
          this.wallpaperService.render( canvas, { ...this.options(), colors: item.colors.length ? item.colors : this.paletteHexes } );
          url = canvas.toDataURL( 'image/jpeg', 0.8 );
        } catch {
          url = '';
        }
        return { name: item.name, note: item.note, url };
      } ),
    } ) );
  }

  private options() {
    return { style: this.style, colors: this.palette.map( p => p.value ), seed: this.artSeed, params: this.params };
  }

  onStyleChange( value: string ): void {
    this.style = this.wallpaperService.styleOf( value );
    // each style has its own three sliders: a new style starts from its defaults
    this.params = defaultParams( this.style );
    this.persistPicture();
    this.analyticsService.logEvent( 'Wallpaper Style Changed', { style: this.style } );
  }

  onSizeChange( value: string ): void {
    this.size = this.wallpaperService.sizeOf( value );
    this.persistPicture();
  }

  onParamsChange( params: number[] ): void {
    this.params = params;
    this.persistPicture();
    const info = styleInfoOf( this.style );
    this.analyticsService.logEvent( 'Wallpaper Params Changed', {
      style: this.style, [ info.params[ 0 ].label ]: params[ 0 ], [ info.params[ 1 ].label ]: params[ 1 ],
    } );
  }

  /** the stage picture opens full screen; the pill under it carries the size and the download */
  openLightbox(): void {
    if ( !this.previewUrl ) return;
    this.lightboxReturnFocus = typeof document !== 'undefined' ? document.activeElement as HTMLElement : null;
    this.lightboxOpen = true;
    // the overlay is the last thing in the page, so focus has to be moved into it by hand for the
    // keyboard: Download is the reason the lightbox is open
    setTimeout( () => this.lightboxDownload?.nativeElement?.focus() );
    this.analyticsService.logEvent( 'Wallpaper Preview Opened', { style: this.style, size: this.size.key } );
  }

  closeLightbox(): void {
    if ( !this.lightboxOpen ) return;
    this.lightboxOpen = false;
    const back = this.lightboxReturnFocus;
    this.lightboxReturnFocus = null;
    setTimeout( () => back?.focus?.() );
  }

  /** Tab stays inside the pill while the lightbox is open */
  @HostListener( 'document:keydown.tab', [ '$event' ] )
  @HostListener( 'document:keydown.shift.tab', [ '$event' ] )
  onLightboxTab( event: KeyboardEvent ): void {
    if ( !this.lightboxOpen ) return;
    const row = document.querySelector( '.cp-lightbox .cp-lightbox-caption-row' );
    const stops = row ? Array.from( row.querySelectorAll<HTMLElement>( 'button:not([disabled])' ) ) : [];
    if ( !stops.length ) return;
    const first = stops[ 0 ];
    const last = stops[ stops.length - 1 ];
    const active = document.activeElement as HTMLElement | null;
    const inside = !!active && stops.indexOf( active ) > -1;
    if ( !inside ) {
      event.preventDefault();
      ( event.shiftKey ? last : first ).focus();
      return;
    }
    if ( event.shiftKey && active === first ) {
      event.preventDefault();
      last.focus();
    } else if ( !event.shiftKey && active === last ) {
      event.preventDefault();
      first.focus();
    }
  }

  @HostListener( 'document:keydown.escape' )
  onEscape(): void {
    if ( this.lightboxOpen ) {
      this.closeLightbox();
    }
  }

  /** a Sizes card: the preview takes that size; the Download button follows */
  selectSize( size: WallpaperSize ): void {
    this.size = size;
    this.persistPicture();
    this.analyticsService.logEvent( 'Wallpaper Size Selected', { size: size.key, width: size.width, height: size.height } );
  }

  private persistPicture(): void {
    this.scheduleRender();
    this.router.navigate( [], {
      relativeTo: this.route,
      queryParams: this.pictureQuery(),
      queryParamsHandling: 'merge',
      replaceUrl: true,
      state: { scrollToTop: false },
    } );
  }

  /** renders the wallpaper at the preset's full size and saves it as a PNG */
  download( size: WallpaperSize = this.size ): void {
    if ( this.downloading ) return;
    this.downloading = size.key;
    // let the button repaint before the (possibly 5K) render blocks the thread
    setTimeout( () => {
      try {
        const canvas = document.createElement( 'canvas' );
        canvas.width = size.width;
        canvas.height = size.height;
        this.wallpaperService.render( canvas, this.options() );
        canvas.toBlob( blob => {
          if ( blob ) {
            const a = document.createElement( 'a' );
            const url = URL.createObjectURL( blob );
            a.href = url;
            a.download = this.fileName( size );
            document.body.appendChild( a );
            a.click();
            a.remove();
            setTimeout( () => URL.revokeObjectURL( url ), 5000 );
          }
          this.zone.run( () => this.downloading = '' );
        }, 'image/png' );
        this.analyticsService.logEvent( 'Wallpaper Downloaded', {
          style: this.style, size: size.key, width: size.width, height: size.height,
          theme: this.foundTheme?.key || null, source: this.currentGenerator || 'custom', colors: this.palette.length,
        } );
      } catch ( e ) {
        console.error( 'Wallpaper render failed:', e );
        this.downloading = '';
      }
    }, 40 );
  }

  private fileName( size: WallpaperSize ): string {
    const who = this.foundTheme?.key ? `${ this.foundTheme.key }-` : '';
    return `codigrate-${ who }${ this.style }-wallpaper-${ size.width }x${ size.height }.png`;
  }

  copyLink(): void {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if ( !url ) return;
    this.clipboard.copy( url );
    this.copied = true;
    if ( this.copiedTimer ) clearTimeout( this.copiedTimer );
    this.copiedTimer = setTimeout( () => this.copied = false, 1500 );
    this.analyticsService.logEvent( 'Wallpaper Link Copied', { style: this.style } );
  }

  /** the size a Sizes card links to: the same picture, that preset */
  sizeParams( size: WallpaperSize ): any {
    return { ...this.route.snapshot.queryParams, ...this.pictureQuery(), size: size.key };
  }

  get currentHexes(): string {
    return this.palette.map( p => p.value.replace( '#', '' ) ).join( '-' );
  }

  get paletteHexes(): string[] {
    return this.palette.map( p => p.value );
  }

  get styleInfo() {
    return WALLPAPER_STYLES.find( s => s.key === this.style ) || WALLPAPER_STYLES[ 0 ];
  }

  // ---------------------------------------------------------------- palette (as the gradient tool)

  private startQueryParams( start: { theme?: any; scheme?: string; initializer?: string; seed?: string } ): any {
    if ( start.theme ) {
      return { theme: start.theme.key };
    }
    const qp: any = { scheme: start.scheme };
    if ( start.initializer ) { qp.initializer = start.initializer; }
    if ( start.seed ) { qp.seed = start.seed; }
    return qp;
  }

  private navigateTo( hexes: string[], queryParams: any, replace = false, skipLocation = false ): void {
    this.editPending = false;
    const params = hexes.map( h => h.replace( '#', '' ) ).join( '-' );
    this.router.navigate( [ URLS.tools.wallpaper + '/' + params ],
      { queryParams: { ...queryParams, ...this.pictureQuery() }, replaceUrl: replace, skipLocationChange: skipLocation, state: { scrollToTop: false } } );
  }

  private applySourceParams( params: any ): void {
    this.foundTheme = params.theme
      ? THEMES.map( l => l.products ).reduce( ( a: any[], b: any[] ) => a.concat( b ), [] ).find( ( p: any ) => p.key === params.theme )
      : null;
    this.initializer = params.initializer
      ? {
        colorName: String( params.initializer ).replace( /-/g, ' ' ),
        value: HEX_CODES[ params.initializer ] ? '#' + HEX_CODES[ params.initializer ] : '',
      }
      : undefined;
    this.scheme = params.scheme || '';
    this.fullSeed = params.seed || '';
  }

  onGenerate( scheme: string ): void {
    this.artSeed = this.wallpaperService.randomSeed();
    if ( scheme === 'theme' || scheme.startsWith( 'theme:' ) ) {
      // 'theme:<key>' = the theme picked in the generator (owner 2026-10-12), 'theme' = another theme each time
      const t = scheme.startsWith( 'theme:' ) ? this.paletteGen.themeByKey( scheme.slice( 6 ) ) : this.paletteGen.randomTheme( this.foundTheme?.key );
      if ( !t.hexes.length ) {
        return;
      }
      this.navigateTo( t.hexes.slice( 0, this.count ), { theme: t.theme?.key } );
      this.analyticsService.logEvent( 'Wallpaper Initialized', { source: 'theme' } );
      return;
    }
    const r = this.paletteGen.build( scheme, this.count );
    if ( !r.hexes.length ) {
      return;
    }
    this.navigateTo( r.hexes, this.startQueryParams( r ) );
    this.analyticsService.logEvent( 'Wallpaper Initialized', { source: r.scheme || scheme } );
  }


  onEditColor( change: { index: number; value: string } ): void {
    const item = this.palette[ change.index ];
    if ( !item ) {
      return;
    }
    item.value = change.value;
    this.buildChannelStats();
    this.scheduleRender();
    this.editPending = true;
    this.editSubject.next();
  }

  drop( event: CdkDragDrop<unknown> ): void {
    moveItemInArray( this.palette, event.previousIndex, event.currentIndex );
    this.buildChannelStats();
    this.navigateTo( this.palette.map( p => p.value ), {} );
  }

  /** the widget's reverse button: the palette in the opposite order, the same style, seed and fields */
  reverseColors(): void {
    if ( this.palette.length < 2 ) return;
    this.palette.reverse();
    this.navigateTo( this.palette.map( p => p.value ), {} );
    this.analyticsService.logEvent( 'Wallpaper Reversed', { count: this.palette.length } );
  }

  addColor(): void {
    if ( this.palette.length >= 10 ) {
      return;
    }
    this.palette.push( { value: this.colorService.generateRandomHexColor() } );
    this.buildChannelStats();
    this.navigateTo( this.palette.map( p => p.value ), {} );
  }

  removeColor( index: number ): void {
    if ( this.palette.length <= 2 ) {
      return;
    }
    this.palette.splice( index, 1 );
    this.buildChannelStats();
    this.navigateTo( this.palette.map( p => p.value ), {} );
  }

  handleImageUpload( event: any ): void {
    const file = event.target.files[ 0 ];
    if ( !file ) {
      return;
    }
    const reader = new FileReader();
    reader.onload = ( e: any ) => {
      const img = new Image();
      img.src = e.target.result;
      img.id = file.name;
      img.onload = async () => {
        try {
          let colors = await extractColors( img.src, {
            pixels: 500, distance: 0.1, saturationDistance: 0, lightnessDistance: 0, hueDistance: 0,
          } );
          this.zone.run( () => {
            colors.sort( ( a, b ) => b.area - a.area );
            if ( colors.length > this.count ) {
              colors = colors.slice( 0, this.count );
            }
            while ( colors.length < 2 ) {
              colors.push( colors[ 0 ] );
            }
            const palette = colors.map( c => ( { value: c.hex } ) );
            this.colorService.sortByHue( palette );
            const canvas = document.createElement( 'canvas' );
            const scale = Math.min( 1, 640 / img.width );
            canvas.width = Math.round( img.width * scale );
            canvas.height = Math.round( img.height * scale );
            const ctx = canvas.getContext( '2d' );
            if ( ctx ) {
              ctx.drawImage( img, 0, 0, canvas.width, canvas.height );
              this.uploadedImg = { src: canvas.toDataURL( 'image/jpeg', 0.85 ), id: file.name };
            }
            this.artSeed = this.wallpaperService.randomSeed();
            this.navigateTo( palette.map( p => p.value ), {} );
            this.analyticsService.logEvent( 'Wallpaper Initialized', { source: 'image' } );
          } );
        } catch ( error ) {
          console.error( 'Error reading image palette:', error );
        }
      };
    };
    reader.readAsDataURL( file );
  }

  // ---------------------------------------------------------------- subtitle

  private themeHref( theme: any ): string {
    for ( const line of THEMES ) {
      if ( line.products.some( ( p: any ) => p.key === theme?.key ) ) {
        return `${ URLS.theme }?product=${ theme.key }&category=${ ( line.name || '' ).toLowerCase() }`;
      }
    }
    return '';
  }

  private colorHref( hex: string ): string {
    const h = ( hex || '' ).replace( '#', '' );
    return h ? `${ URLS.tools.color }/${ h }` : '';
  }

  private get themeName(): string {
    const name = ( this.foundTheme?.name || '' ).trim().replace( /\s*theme\s*$/i, '' );
    return name ? `${ name } Theme` : '';
  }

  get currentGenerator(): string {
    if ( this.uploadedImg ) { return 'image'; }
    if ( this.foundTheme ) { return 'theme'; }
    return this.scheme || '';
  }

  /** over every section card, the style and the source (owner 2026-10-12): "Synthwave · Istanbul Theme",
   *  "Mesh · Medium Purple", "Dunes · seed-..." (a middle dot, the site uses no dashes) */
  get sourceLabel(): string {
    const s = this.subtitle;
    let source: string;
    switch ( s.kind ) {
      case 'theme': source = s.themeName || 'Theme'; break;
      case 'harmony': source = s.name || ''; break;
      case 'image': source = s.imageName || 'Uploaded Image'; break;
      case 'full': source = s.seed ? 'seed-' + s.seed : 'Random'; break;
      default: source = s.seed ? 'seed-' + s.seed : 'Custom';
    }
    return this.styleInfo.label + ' · ' + source;
  }

  // the scheme in Title Case for the hero title ("Split Complementary Wallpaper", owner 2026-10-12)
  get schemeTitle(): string {
    return ( this.subtitle.scheme || '' ).trim().replace( /\b\w/g, ch => ch.toUpperCase() );
  }

  /** the page's top band (page-band.scss): the palette's colours side by side, each a little, fading down to
   *  the page (owner 2026-10-12, as on the palette tool) */
  @HostBinding( 'style.--band' ) get band(): string {
    const first = this.palette.find( p => /^#[0-9a-f]{6}$/i.test( p.value || '' ) )?.value;
    return `color-mix(in oklab, ${ first || 'var(--theme-color)' } 55%, var(--background-color))`;
  }

  @HostBinding( 'style.--band-row' ) get bandRow(): string {
    const colors = this.palette.map( p => p.value ).filter( c => /^#[0-9a-f]{6}$/i.test( c || '' ) );
    if ( colors.length < 2 ) {
      return `linear-gradient(180deg, ${ this.band } 0%, var(--background-color) 100%)`;
    }
    const stops = colors.map( c => `color-mix(in oklab, ${ c } 55%, var(--background-color))` );
    return `linear-gradient(90deg, ${ stops.join( ', ' ) })`;
  }

  get subtitle(): PaletteSubtitle {
    if ( this.uploadedImg ) {
      return { kind: 'image', imageSrc: this.uploadedImg.src, imageName: this.uploadedImg.id };
    }
    if ( this.foundTheme ) {
      return { kind: 'theme', themeName: this.themeName, logo: this.foundTheme.logo, themeHref: this.themeHref( this.foundTheme ) };
    }
    const sourced = [ 'complementary', 'split complementary', 'triadic', 'tetradic', 'analogous', 'monochrome', 'warm', 'cool' ];
    if ( sourced.includes( this.scheme ) && this.initializer ) {
      const name = ( this.initializer.colorName || '' ).replace( /\b\w/g, c => c.toUpperCase() );
      return { kind: 'harmony', scheme: this.scheme, name, color: this.initializer.value, colorHref: this.colorHref( this.initializer.value ) };
    }
    if ( this.scheme === 'full' ) {
      return { kind: 'full', seed: this.fullSeed || '' };
    }
    return { kind: 'custom', seed: this.palette.length ? this.paletteGen.paletteSeed( this.palette.map( p => p.value ) ) : '' };
  }

  // ---------------------------------------------------------------- seo

  /** set while the page is a style landing (/tools/wallpaper/mesh ...): the URL stays, the head is the style's */
  styleLandingKey = '';

  private setStyleLandingSeo( info: { key: string; label: string; blurb: string } ): void {
    setTimeout( () => this.seoService.setMetaData( {
      title: `${ info.label } Wallpaper Generator - Free ${ info.label } Wallpapers From Any Palette | Codigrate`,
      description: `Free ${ info.label.toLowerCase() } wallpaper generator by Codigrate. ${ info.blurb }. Pick a palette and a style, then download the ${ info.label.toLowerCase() } wallpaper for 4K, 5K, ultrawide, MacBook, iPhone, Android and iPad.`,
      keywords: [ `${ info.label.toLowerCase() } wallpaper`, `${ info.label.toLowerCase() } wallpaper generator`, `${ info.label.toLowerCase() } background`, 'wallpaper generator', '4k wallpaper', 'iphone wallpaper', 'codigrate' ],
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } ) );
  }

  /** the landing page's static head (the navigation interceptor would otherwise append the hexes to the title) */
  private setLandingSeo(): void {
    this.seoService.setMetaData( {
      title: 'Wallpaper Generator - Free Procedural Wallpapers From Any Color Palette | Codigrate',
      description: 'Free wallpaper generator by Codigrate. Turn any color palette into a mesh, wave, aurora, low poly, stripe or orb wallpaper and download it for 4K, 5K, ultrawide, MacBook, iPhone, Android and iPad in one click.',
      keywords: [ 'wallpaper generator', 'procedural wallpaper', 'gradient wallpaper', 'mesh gradient wallpaper', 'aurora wallpaper', 'low poly wallpaper', '4k wallpaper', 'iphone wallpaper', 'color palette wallpaper', 'codigrate' ],
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
  }

  /** a shared / theme wallpaper gets its own title and description in the head */
  private refreshSeo(): void {
    const path = this.location.path().split( '?' )[ 0 ];
    if ( !path.startsWith( URLS.tools.wallpaper + '/' ) ) {
      // the blank landing renders in place at the bare URL: keep its static head
      setTimeout( () => this.setLandingSeo() );
      return;
    }
    // a style landing page renders its picture at its own URL, so it keeps its own indexable head
    const landing = WALLPAPER_STYLES.find( st => st.key === path.slice( URLS.tools.wallpaper.length + 1 ).toLowerCase() );
    if ( landing ) {
      this.setStyleLandingSeo( landing );
      return;
    }
    const styleLabel = this.styleInfo.label;
    const who = this.foundTheme ? `${ this.themeName }` : `a ${ this.palette.length }-color palette`;
    const title = this.foundTheme
      ? `${ this.themeName } ${ styleLabel } Wallpaper - 4K, 5K, iPhone & Android | Codigrate`
      : `${ styleLabel } Wallpaper From ${ this.palette.length } Colors - Free Download | Codigrate`;
    const description = `A procedural ${ styleLabel.toLowerCase() } wallpaper generated from ${ who } (${ this.palette.map( p => p.value.toUpperCase() ).join( ', ' ) }). ${ this.styleInfo.blurb }. Download it for 4K, 5K, ultrawide, MacBook, iPhone, Android or iPad.`;
    this.seoService.setMetaData( {
      title,
      description,
      keywords: [ 'wallpaper generator', `${ styleLabel.toLowerCase() } wallpaper`, ...( this.foundTheme ? [ `${ this.themeName.toLowerCase() } wallpaper` ] : [] ), '4k wallpaper', 'iphone wallpaper', 'color palette wallpaper', 'codigrate' ],
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
  }

  // ---------------------------------------------------------------- analysis (ported identically from the palette / gradient tools)


  // Aggregate the palette into overall channel stats (RGB weight + average HSL /
  // HSV / CMYK), the same section the palette tool shows.
  private buildChannelStats(): void {
    // The analysis sections (Harmony, Tints/Shades, Temperature, Color Vision,
    // Matching Themes) rebuild whenever the palette does.
    this.buildAnalysis();
    const hexes = this.palette.map( p => p.value ).filter( Boolean );
    if ( hexes.length === 0 ) {
      this.channelStats = null;
      return;
    }
    let sumR = 0, sumG = 0, sumB = 0, sumS = 0, sumL = 0, hx = 0, hy = 0;
    let sumSv = 0, sumV = 0, sumC = 0, sumM = 0, sumY = 0, sumK = 0;
    for ( const hex of hexes ) {
      const num = parseInt( hex.replace( '#', '' ), 16 );
      const r = ( num >> 16 ) & 255;
      const g = ( num >> 8 ) & 255;
      const b = num & 255;
      sumR += r;
      sumG += g;
      sumB += b;
      const hsl = this.colorService.hexToHsl( hex.replace( '#', '' ) );
      sumS += hsl.s;
      sumL += hsl.l;
      const rad = ( hsl.h * Math.PI ) / 180;
      hx += Math.cos( rad );
      hy += Math.sin( rad );

      const rf = r / 255, gf = g / 255, bf = b / 255;
      const max = Math.max( rf, gf, bf ), min = Math.min( rf, gf, bf );
      sumV += max * 100;
      sumSv += ( max === 0 ? 0 : ( max - min ) / max ) * 100;
      const k = 1 - max;
      sumK += k * 100;
      if ( k < 1 ) {
        sumC += ( ( 1 - rf - k ) / ( 1 - k ) ) * 100;
        sumM += ( ( 1 - gf - k ) / ( 1 - k ) ) * 100;
        sumY += ( ( 1 - bf - k ) / ( 1 - k ) ) * 100;
      }
    }
    const n = hexes.length;
    const total = ( sumR + sumG + sumB ) || 1;
    let avgH = ( Math.atan2( hy / n, hx / n ) * 180 ) / Math.PI;
    if ( avgH < 0 ) { avgH += 360; }
    const avgS = sumS / n;
    const avgL = sumL / n;
    const avgSv = sumSv / n;
    const avgV = sumV / n;
    const avgC = sumC / n, avgM = sumM / n, avgYy = sumY / n, avgK = sumK / n;
    const weight = ( x: number ) => Math.round( ( x / total ) * 100 );
    this.channelStats = {
      rgb: [
        { label: 'R', width: weight( sumR ), text: weight( sumR ) + '%', color: '#e0584f' },
        { label: 'G', width: weight( sumG ), text: weight( sumG ) + '%', color: '#3fb27a' },
        { label: 'B', width: weight( sumB ), text: weight( sumB ) + '%', color: '#4a86e8' },
      ],
      hsl: [
        { label: 'H', width: ( avgH / 360 ) * 100, text: Math.round( avgH ) + '°', color: `hsl(${ avgH }, 75%, 55%)` },
        { label: 'S', width: avgS, text: Math.round( avgS ) + '%', color: `hsl(${ avgH }, ${ avgS }%, 55%)` },
        { label: 'L', width: avgL, text: Math.round( avgL ) + '%', color: `hsl(0, 0%, ${ avgL }%)` },
      ],
      hsv: [
        { label: 'H', width: ( avgH / 360 ) * 100, text: Math.round( avgH ) + '°', color: `hsl(${ avgH }, 75%, 55%)` },
        { label: 'S', width: avgSv, text: Math.round( avgSv ) + '%', color: `hsl(${ avgH }, ${ avgSv }%, 55%)` },
        { label: 'V', width: avgV, text: Math.round( avgV ) + '%', color: `hsl(${ avgH }, 40%, ${ Math.max( 20, avgV ) }%)` },
      ],
      cmyk: [
        { label: 'C', width: avgC, text: Math.round( avgC ) + '%', color: '#22b3c8' },
        { label: 'M', width: avgM, text: Math.round( avgM ) + '%', color: '#cf2e8a' },
        { label: 'Y', width: avgYy, text: Math.round( avgYy ) + '%', color: '#e6c100' },
        { label: 'K', width: avgK, text: Math.round( avgK ) + '%', color: '#3a3a3a' },
      ],
    };
  }

  // ===================== Analysis sections (ported from the palette tool) =====================

  // Run every analysis build (harmony, tints/shades, temperature, color vision,
  // matching themes) off the current palette.
  private buildAnalysis(): void {
    this.protanomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'protanomaly' );
    this.deuteranomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'deuteranomaly' );
    this.tritanomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'tritanomaly' );
    this.protanopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'protanopia' );
    this.deuteranopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'deuteranopia' );
    this.tritanopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'tritanopia' );
    this.achromatomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'achromatomaly' );
    this.achromatopsiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'achromatopsia' );
    this.harmonyResult = this.analyzePaletteHarmony();
    this.buildHarmonyScores();
    this.buildShadeTintTone();
    this.buildTemperature();
    this.buildRelatedThemes();
  }

  /** Valid #RRGGBB colors from the current palette, in order. */
  private pickHeroColors(): string[] {
    const out: string[] = [];
    for ( const item of this.palette || [] ) {
      const raw = item?.value;
      if ( typeof raw !== 'string' ) {
        continue;
      }
      const hex = raw.startsWith( '#' ) ? raw : '#' + raw;
      if ( /^#[0-9a-fA-F]{6}$/.test( hex ) ) {
        out.push( hex );
      }
    }
    return out;
  }

  public analyzePaletteHarmony() {
    const rgbPalette = this.palette.map( color => this.colorService.hexToRgb( color.value ) );
    const hsvPalette = rgbPalette.map( rgb => this.colorService.rgbToHsv( rgb ) );
    const hues = hsvPalette.map( hsv => hsv.h );

    let complementaryCount = 0;
    let analogousCount = 0;
    let triadicCount = 0;
    let warmCount = 0;
    let coolCount = 0;

    const huePairs = new Set();
    const hueTriplets = new Set();

    this.temperatures = [];
    this.analogous = [];
    this.complementaries = [];
    this.triadics = [];
    for ( let i = 0; i < hues.length; i++ ) {
      this.analogous.push( 0 );
      this.complementaries.push( 0 );
      this.triadics.push( 0 );
    }

    for ( let i = 0; i < hues.length; i++ ) {
      if ( this.colorService.isWarm( hues[ i ] ) ) {
        warmCount++;
        this.temperatures.push( 'warm' );
      } else if ( this.colorService.isCool( hues[ i ] ) ) {
        this.temperatures.push( 'cool' );
        coolCount++;
      }
      for ( let j = i + 1; j < hues.length; j++ ) {
        if ( !huePairs.has( `${ i },${ j }` ) && !huePairs.has( `${ j },${ i }` ) ) {
          if ( this.colorService.isComplementary( hues[ i ], hues[ j ] ) ) {
            this.complementaries[ i ] = this.complementaries[ i ] + 1;
            this.complementaries[ j ] = this.complementaries[ j ] + 1;
          }
          if ( this.colorService.isAnalogous( hues[ i ], hues[ j ] ) ) {
            this.analogous[ i ] = this.analogous[ i ] + 1;
            this.analogous[ j ] = this.analogous[ j ] + 1;
          }
          huePairs.add( `${ i },${ j }` );
        }
      }
    }

    for ( let i = 0; i < hues.length - 2; i++ ) {
      for ( let j = i + 1; j < hues.length - 1; j++ ) {
        for ( let k = j + 1; k < hues.length; k++ ) {
          const triplet = [ hues[ i ], hues[ j ], hues[ k ] ].sort().join( ',' );
          if ( !hueTriplets.has( triplet ) ) {
            if ( this.colorService.isTriadic( [ hues[ i ], hues[ j ], hues[ k ] ] ) ) {
              this.triadics[ i ] = this.triadics[ i ] + 1;
              this.triadics[ j ] = this.triadics[ j ] + 1;
              this.triadics[ k ] = this.triadics[ k ] + 1;
            }
            hueTriplets.add( triplet );
          }
        }
      }
    }

    for ( let i = 0; i < hues.length; i++ ) {
      analogousCount += this.analogous[ i ];
      complementaryCount += this.complementaries[ i ];
      triadicCount += this.triadics[ i ];
    }

    const temperatureCount = Math.max( warmCount, coolCount );
    let temperatureDecision = '';
    if ( warmCount > coolCount ) {
      temperatureDecision = 'warm';
    } else if ( warmCount < coolCount ) {
      temperatureDecision = 'cool';
    } else {
      temperatureDecision = 'equal';
    }

    return [
      { name: 'Analogous', count: analogousCount, max: ( this.palette.length * ( this.palette.length - 1 ) ) / 2 },
      { name: 'Complementary', count: complementaryCount, max: ( this.palette.length * ( this.palette.length - 1 ) ) / 2 },
      { name: 'Triadic', count: triadicCount, max: ( this.palette.length * ( this.palette.length - 1 ) * ( this.palette.length - 2 ) ) / 6 },
      { name: 'Temperature', count: temperatureCount, decision: temperatureDecision, max: this.palette.length },
    ];
  }

  private buildHarmonyScores(): void {
    const hexes = this.pickHeroColors();
    const n = hexes.length;
    const hues = hexes.map( h => this.colorService.hexToHsl( h.replace( '#', '' ) ).h );

    const dist = ( a: number, b: number ): number => {
      const d = Math.abs( a - b ) % 360;
      return d > 180 ? 360 - d : d;
    };
    const pair = ( ok: ( a: number, b: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          if ( ok( hues[ i ], hues[ j ] ) ) { counts[ i ]++; counts[ j ]++; }
        }
      }
      return counts;
    };
    const triple = ( ok: ( a: number, b: number, c: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          for ( let k = j + 1; k < n; k++ ) {
            if ( ok( hues[ i ], hues[ j ], hues[ k ] ) ) { counts[ i ]++; counts[ j ]++; counts[ k ]++; }
          }
        }
      }
      return counts;
    };
    const quad = ( ok: ( a: number, b: number, c: number, d: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          for ( let k = j + 1; k < n; k++ ) {
            for ( let l = k + 1; l < n; l++ ) {
              if ( ok( hues[ i ], hues[ j ], hues[ k ], hues[ l ] ) ) {
                counts[ i ]++; counts[ j ]++; counts[ k ]++; counts[ l ]++;
              }
            }
          }
        }
      }
      return counts;
    };

    const isComp = ( a: number, b: number ) => dist( a, b ) >= 156;
    // Monochrome and analogous kept DISJOINT (same hue vs neighbouring distinct hue)
    // so they no longer score the same.
    const isMono = ( a: number, b: number ) => dist( a, b ) <= 12;
    const isAnalog = ( a: number, b: number ) => dist( a, b ) > 12 && dist( a, b ) <= 40;
    const isTriad = ( a: number, b: number, c: number ) =>
      dist( a, b ) >= 90 && dist( a, b ) <= 150 &&
      dist( b, c ) >= 90 && dist( b, c ) <= 150 &&
      dist( a, c ) >= 90 && dist( a, c ) <= 150;
    const isSplit = ( a: number, b: number, c: number ) => {
      const tri = [ a, b, c ];
      for ( let base = 0; base < 3; base++ ) {
        const x = tri[ ( base + 1 ) % 3 ];
        const y = tri[ ( base + 2 ) % 3 ];
        if ( dist( tri[ base ], x ) >= 126 && dist( tri[ base ], x ) <= 174 &&
             dist( tri[ base ], y ) >= 126 && dist( tri[ base ], y ) <= 174 &&
             dist( x, y ) >= 36 && dist( x, y ) <= 90 ) {
          return true;
        }
      }
      return false;
    };
    const isTetra = ( a: number, b: number, c: number, d: number ) => {
      const pairings = [
        [ [ a, b ], [ c, d ] ],
        [ [ a, c ], [ b, d ] ],
        [ [ a, d ], [ b, c ] ],
      ];
      return pairings.some( p => isComp( p[ 0 ][ 0 ], p[ 0 ][ 1 ] ) && isComp( p[ 1 ][ 0 ], p[ 1 ][ 1 ] ) );
    };

    const chroma = hexes.map( h => {
      const v = parseInt( h.replace( '#', '' ), 16 );
      const r = ( v >> 16 ) & 255, g = ( v >> 8 ) & 255, b = v & 255;
      return Math.max( r, g, b ) - Math.min( r, g, b );
    } );
    const lowChroma = ( i: number ): boolean => chroma[ i ] <= 28;

    const pairIdx = ( ok: ( i: number, j: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          if ( ok( i, j ) ) { counts[ i ]++; counts[ j ]++; }
        }
      }
      return counts;
    };

    const schemes: { name: string; description: string; counts: number[] }[] = [
      { name: 'Analogous', description: 'Neighbouring hues. Calm, cohesive, easy on the eyes.', counts: pairIdx( ( i, j ) => !lowChroma( i ) && !lowChroma( j ) && isAnalog( hues[ i ], hues[ j ] ) ) },
      { name: 'Monochrome', description: 'One hue, varied in lightness. Quiet, clean, simple to balance.', counts: pairIdx( ( i, j ) => ( lowChroma( i ) && lowChroma( j ) ) || isMono( hues[ i ], hues[ j ] ) ) },
      { name: 'Complementary', description: 'The opposite hue. Maximum contrast, one bold accent.', counts: pair( isComp ) },
      { name: 'Split Complementary', description: 'The two hues beside the opposite. Softer tension, more range.', counts: triple( isSplit ) },
      { name: 'Triadic', description: 'Three evenly spaced hues. Balanced but lively.', counts: triple( isTriad ) },
      { name: 'Tetradic', description: 'Two complementary pairs. The richest scheme, hardest to balance.', counts: quad( isTetra ) },
    ];

    this.harmonyScores = schemes.map( s => ( {
      name: s.name,
      description: s.description,
      // The number of palette colours taking part in this scheme (a plain count,
      // not a percentage). Same measure across every tool and the theme page.
      score: s.counts.filter( c => c > 0 ).length,
      counts: s.counts,
    } ) );
  }

  private buildShadeTintTone(): void {
    const hexes = this.pickHeroColors();
    const EXTRA = 4;
    const rowsToward = ( endHex: string ): string[][] => {
      const ramps = hexes.map( base => this.colorService.generateShades( base, endHex, EXTRA + 1 ) );
      const rows: string[][] = [];
      for ( let step = 0; step <= EXTRA; step++ ) {
        rows.push( ramps.map( r => r[ step ] ) );
      }
      return rows;
    };
    this.shadeTintTone = [
      { title: 'Tints', rows: rowsToward( 'ffffff' ) },
      { title: 'Shades', rows: rowsToward( '000000' ) },
      { title: 'Tones', rows: rowsToward( '808080' ) },
    ];
  }

  private buildTemperature(): void {
    const hexes = this.pickHeroColors();
    if ( hexes.length === 0 ) {
      this.temperatureVariants = [];
      return;
    }
    const EXTRA = 4;
    const STRIDE = 2;
    const toRows = ( ramps: string[][] ): string[][] => {
      const rows: string[][] = [];
      for ( let step = 0; step <= EXTRA; step++ ) {
        rows.push( ramps.map( r => r[ step * STRIDE ] ) );
      }
      return rows;
    };
    const span = EXTRA * STRIDE + 1;
    this.temperatureVariants = [
      { title: 'Warmer', rows: toRows( hexes.map( h => this.colorService.generateWarmerColors( h, span ) ) ) },
      { title: 'Cooler', rows: toRows( hexes.map( h => this.colorService.generateCoolerColors( h, span ) ) ) },
    ];
  }

  private buildRelatedThemes(): void {
    const mine = this.pickHeroColors().map( h => this.colorService.hexToHsl( h.replace( '#', '' ) ) );
    if ( mine.length === 0 ) {
      this.relatedThemeList = [];
      return;
    }
    const dist = ( a: any, b: any ): number => {
      let dh = Math.abs( a.h - b.h );
      if ( dh > 180 ) { dh = 360 - dh; }
      const satW = Math.min( a.s, b.s ) / 100;
      const hueTerm = dh * satW;
      return Math.sqrt( hueTerm * hueTerm + 0.12 * ( a.s - b.s ) * ( a.s - b.s ) + 0.10 * ( a.l - b.l ) * ( a.l - b.l ) );
    };
    const scored: { product: Product; category: string; d: number }[] = [];
    for ( const line of THEMES ) {
      for ( const theme of line.products ) {
        const pal = ( theme.palette || [] )
          .filter( ( p: string ) => typeof p === 'string' && /^#?[0-9a-fA-F]{6}$/.test( p.replace( '#', '' ) ) )
          .map( ( p: string ) => this.colorService.hexToHsl( p.replace( '#', '' ) ) );
        if ( pal.length === 0 ) { continue; }
        let sum = 0;
        for ( const c of mine ) {
          let best = Infinity;
          for ( const t of pal ) {
            const d = dist( c, t );
            if ( d < best ) { best = d; }
          }
          sum += best;
        }
        scored.push( { product: theme, category: line.name, d: sum / mine.length } );
      }
    }
    scored.sort( ( a, b ) => a.d - b.d );
    const best = scored.length ? scored[ 0 ].d : 0;
    const MARGIN = 1;
    const close = scored.filter( ( s, i ) => i === 0 || s.d <= best + MARGIN );
    this.relatedThemeList = close.slice( 0, 3 ).map( ( { d, ...rest } ) => rest );
  }

  // Dot level from the share of the palette that takes part (count / total).
  harmonyLevel( count: number, total: number ): string {
    const frac = total > 0 ? count / total : 0;
    if ( frac >= 0.67 ) { return 'high'; }
    if ( frac >= 0.34 ) { return 'mid'; }
    return 'low';
  }

  // ----- Harmony as gradient bars (gradient tool: a single thin gradient bar per
  // scheme instead of discrete swatches) -----

  // The FULL-palette gradient, but every colour that does NOT take part in the
  // scheme (count <= 0) is greyed out — so the bar reads at a glance like the old
  // swatch row (lit vs muted), and reflects the score granularly instead of all or
  // nothing.
  harmonyBar( counts: number[] ): string {
    const cols = this.palette.map( ( p, i ) =>
      ( counts && counts[ i ] > 0 ) ? p.value : this.toGrey( p.value ) );
    return this.barFrom( cols );
  }

  // Same idea for Temperature: colours that don't match the dominant temperature
  // are greyed (unless the palette is temperature-balanced, when all stay lit).
  temperatureBar(): string {
    const decision = this.harmonyResult[ 3 ]?.decision;
    const cols = this.palette.map( ( p, i ) =>
      ( decision && decision !== 'equal' && this.temperatures[ i ] !== decision )
        ? this.toGrey( p.value )
        : p.value );
    return this.barFrom( cols );
  }

  // A gradient bar for a tints / shades / tones / temperature / color-vision row
  // (same look as the harmony bars): the row's colours spread left-to-right.
  shadeBar( row: string[] ): string {
    return this.barFrom( row || [] );
  }

  // The full palette as a left-to-right bar (the Color Vision "Original" row).
  originalBar(): string {
    return this.barFrom( this.palette.map( p => p.value ) );
  }

  // Horizontal position (%) of colour i along the bar — the same even spacing the
  // gradient uses — so the per-colour score labels sit right under their colour.
  harmonyStopLeft( i: number ): number {
    const n = this.palette.length;
    return n > 1 ? ( i / ( n - 1 ) ) * 100 : 50;
  }

  // Mobile font (px) for the per-colour analysis labels (hex codes / scores /
  // warm-cool). Picks the largest size, capped, at which a label still fits the
  // spacing between adjacent stops, so the row stays HORIZONTAL and never overlaps
  // however many colours the palette has. Read as a CSS var only under the phone
  // breakpoint (see .gr-harmony-label in the scss); desktop keeps its 12px.
  get analysisLabelFont(): number {
    const n = this.palette.length || 1;
    if ( n <= 1 ) { return 12; }
    // ~324px is about as wide as the bar gets on a phone.
    const spacing = 324 / ( n - 1 );
    // The end labels stay edge-aligned (so they never clip off the card), which
    // lets a label use at most ~2/3 of the gap to its neighbour before they touch.
    // A 7-char monospace hex is ~4.04em wide; size the font to honour that, capped
    // at the desktop 12px so few-colour palettes look exactly like before.
    const fit = ( ( 2 / 3 ) * spacing - 3 ) / 4.04;
    return Math.max( 6, Math.min( 12, Math.round( fit * 10 ) / 10 ) );
  }

  // A muted grey for a non-participating colour: its own luminance, washed toward
  // a light grey so it reads as "inactive" (matching the old grayscale swatch).
  private toGrey( hex: string ): string {
    const v = parseInt( ( hex || '' ).replace( '#', '' ), 16 );
    if ( isNaN( v ) ) { return '#c0c0c0'; }
    const r = ( v >> 16 ) & 255, g = ( v >> 8 ) & 255, b = v & 255;
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    const m = Math.round( lum * 0.5 + 192 * 0.5 );
    const h = m.toString( 16 ).padStart( 2, '0' );
    return `#${ h }${ h }${ h }`;
  }

  private barFrom( cols: string[] ): string {
    if ( cols.length === 0 ) { return 'transparent'; }
    if ( cols.length === 1 ) { return cols[ 0 ]; }
    const n = cols.length;
    const stops = cols.map( ( c, i ) => `${ c } ${ ( i / ( n - 1 ) * 100 ).toFixed( 2 ) }%` ).join( ', ' );
    return `linear-gradient(90deg, ${ stops })`;
  }

  colorVisionGroups(): { group: string; items: { name: string; note: string; colors: string[] }[] }[] {
    return [
      {
        group: 'Protan (red)',
        items: [
          { name: 'Protanomaly', note: 'Shifted L-cone (long-wavelength) sensitivity. Reds desaturate and darken.', colors: this.protanomalyColors },
          { name: 'Protanopia', note: 'No functional L-cones. Red and green hues are confused and reds appear dim.', colors: this.protanopiaColors },
        ],
      },
      {
        group: 'Deutan (green)',
        items: [
          { name: 'Deuteranomaly', note: 'Shifted M-cone (medium-wavelength) sensitivity. The most prevalent deficiency.', colors: this.deuteranomalyColors },
          { name: 'Deuteranopia', note: 'No functional M-cones. Red and green hues are confused at normal luminance.', colors: this.deuteranopiaColors },
        ],
      },
      {
        group: 'Tritan (blue)',
        items: [
          { name: 'Tritanomaly', note: 'Shifted S-cone (short-wavelength) sensitivity. Blue and green converge.', colors: this.tritanomalyColors },
          { name: 'Tritanopia', note: 'No functional S-cones. Blue and yellow hues are confused.', colors: this.tritanopiaColors },
        ],
      },
      {
        group: 'Monochromacy (no color)',
        items: [
          { name: 'Achromatomaly', note: 'Reduced function across all three cone types, yielding a heavily desaturated image.', colors: this.achromatomalyColors },
          { name: 'Achromatopsia', note: 'No functional cones. Perception reduces to luminance, seen as grayscale.', colors: this.achromatopsiaColors },
        ],
      },
    ];
  }

  copyText( text: string, key: string ): void {
    this.clipboard.copy( text );
    this.copiedKey = key;
    if ( this.copiedKeyTimer ) { clearTimeout( this.copiedKeyTimer ); }
    this.copiedKeyTimer = setTimeout( () => ( this.copiedKey = '' ), 1200 );
  }

  openRouteInNewTab( route: string ): void {
    const url = this.router.serializeUrl( this.router.createUrlTree( [ route ] ) );
    window.open( url, '_blank' );
  }

  // ----- Gradient render -----

  // ---------------------------------------------------------------- page

  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 )
      ? document.body
      : document.documentElement;

    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.scrollActivePillToStart();
      this.tweenScroll( scroller, 0 );
      return;
    }

    const el = document.getElementById( 'sec-' + key );
    if ( !el ) {
      return;
    }
    this.activeSection = key;
    this.scrollActivePillToStart();

    // clear the nav and the sticky chips' bar under it (owner 2026-10-12)
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    const target = Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset );
    this.tweenScroll( scroller, target );
  }

  private scrollActivePillToStart(): void {
    setTimeout( () => {
      const strip = document.querySelector( '.cp-chipbar-row' ) as HTMLElement | null;
      if ( !strip || strip.scrollWidth <= strip.clientWidth ) {
        return;
      }
      const active = strip.querySelector( '.pl-chip.on' ) as HTMLElement | null;
      if ( !active ) {
        return;
      }
      const sr = strip.getBoundingClientRect();
      const ar = active.getBoundingClientRect();
      const target = strip.scrollLeft + ( ar.left - sr.left ) - ( sr.width - ar.width ) / 2;
      strip.scrollTo( { left: Math.max( 0, target ), behavior: 'smooth' } );
    } );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const duration = 420;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / duration );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) {
        requestAnimationFrame( step );
      }
    };
    requestAnimationFrame( step );
  }
}
